import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {{ id: number, name: string, group: string, price: number }} Plan
 * @typedef {{ id: number, title: string, price: number, available: boolean, plans: Plan[] }} Variant
 * @typedef {{ card: HTMLElement, variant: Variant }} Pick
 * @typedef {{ id: number, quantity: number, properties: Record<string, string>, selling_plan?: number }} CartItem
 */

/**
 * Build-your-own set. Tracks picks, shows the order slip (picked lines, tier
 * progress, set total), and adds every pick to the cart in one request, with an
 * Appstle selling plan on each line when the customer subscribes.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLElement} status
 * @property {HTMLElement} [announce]
 * @property {HTMLElement} lines
 * @property {HTMLTemplateElement} lineTemplate
 * @property {HTMLElement} [empty]
 * @property {HTMLElement} compare
 * @property {HTMLElement} [compareRow]
 * @property {HTMLElement} [compareLabel]
 * @property {HTMLElement} [fullPriceNote]
 * @property {HTMLElement} [shippingRow]
 * @property {HTMLElement} total
 * @property {HTMLElement} [savings]
 * @property {HTMLAnchorElement} [progress]
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} error
 *
 * @extends {Component<Refs>}
 */
class SetBuilder extends Component {
  requiredRefs = ['form', 'status', 'lines', 'lineTemplate', 'compare', 'total', 'submit', 'error'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {IntersectionObserver | null} */
  #slipObserver = null;

  /** True while the summary is on screen or scrolled past (above the viewport). */
  #slipReached = false;

  /** Picks that were just added to the cart; the progress bar stays hidden until they change. */
  #addedPicks = '';

  /** Typewriter: watches copy and the slip's lines until they've been typed. */
  #typeObserver = /** @type {IntersectionObserver | null} */ (null);

  /** Blocks waiting their turn; one types at a time, like one typewriter. */
  #typeQueue = /** @type {HTMLElement[]} */ ([]);

  #typing = false;
  #typeFrame = 0;
  #linesInView = false;

  /** Slip lines already written out, so re-renders don't retype them. */
  #writtenLines = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#observeSlip();
    this.#setUpTypewriter();
    this.#update();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeSlip();
    this.#setUpTypewriter();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
    this.#slipObserver?.disconnect();
    this.#slipObserver = null;
    this.#stopTypewriter();
    document.documentElement.classList.remove('set-builder-in-progress');
  }

  /** Homepage only (section setting); skipped in the theme editor and for reduced motion. */
  get #typewriter() {
    return (
      this.hasAttribute('data-typewriter') &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      !(/** @type {any} */ (window).Shopify?.designMode)
    );
  }

  /** Hides each copy block's letters and types them out the first time it scrolls into view. */
  #setUpTypewriter() {
    this.#stopTypewriter();
    if (!this.#typewriter) return;

    this.#typeObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const target = /** @type {HTMLElement} */ (entry.target);
          if (target === this.refs.lines) {
            this.#linesInView = entry.isIntersecting;
            if (entry.isIntersecting) this.#queueLines();
          } else if (entry.isIntersecting) {
            this.#typeObserver?.unobserve(target);
            this.#queueType(target);
          }
        }
      },
      { rootMargin: '0px 0px -12% 0px' }
    );

    for (const block of this.querySelectorAll('.set-builder__text, .set-builder__notes')) {
      if (!(block instanceof HTMLElement) || block.dataset.typewriter) continue;
      if (!splitLetters(block)) continue;
      block.dataset.typewriter = 'waiting';
      this.#typeObserver.observe(block);
    }
    this.#typeObserver.observe(this.refs.lines);
  }

  /** Stops typing and prints whatever was still waiting. */
  #stopTypewriter() {
    this.#typeObserver?.disconnect();
    this.#typeObserver = null;
    cancelAnimationFrame(this.#typeFrame);
    this.#typing = false;
    this.#typeQueue = [];
    for (const letter of this.querySelectorAll('.set-builder__letter:not(.is-typed)')) {
      letter.classList.add('is-typed');
    }
    for (const line of this.querySelectorAll('.set-builder__line.is-unwritten')) {
      line.classList.remove('is-unwritten');
    }
  }

  /** @param {HTMLElement} target */
  #queueType(target) {
    if (this.#typeQueue.includes(target)) return;
    this.#typeQueue.push(target);
    if (!this.#typing) this.#typeNext();
  }

  /** Queues the slip lines that haven't been written yet. */
  #queueLines() {
    for (const line of this.refs.lines.querySelectorAll('.set-builder__line.is-unwritten')) {
      if (line instanceof HTMLElement) this.#queueType(line);
    }
  }

  async #typeNext() {
    const target = this.#typeQueue.shift();
    if (!target) {
      this.#typing = false;
      return;
    }
    this.#typing = true;

    // Re-rendered slip lines are replaced by already-written copies; skip the old node.
    if (target.isConnected) {
      const letters = [...target.querySelectorAll('.set-builder__letter:not(.is-typed)')];

      if (target.classList.contains('set-builder__line')) {
        if (target.dataset.lineKey) this.#writtenLines.add(target.dataset.lineKey);
        target.classList.remove('is-unwritten');
        // Let the tick draw before the name is typed beside it.
        await new Promise((resolve) => setTimeout(resolve, TICK_DRAW_MS));
      }

      await this.#typeLetters(letters);
      target.dataset.typewriter = 'typed';
    }

    this.#typeNext();
  }

  /**
   * Prints letters one at a time with a slightly uneven beat, like keys
   * being struck. Long blocks speed up so no block takes more than a moment.
   * @param {Element[]} letters
   * @returns {Promise<void>}
   */
  #typeLetters(letters) {
    if (!letters.length) return Promise.resolve();

    const beat = Math.max(TYPE_MIN_MS, Math.min(TYPE_BEAT_MS, TYPE_MAX_TOTAL_MS / letters.length));
    const times = [];
    let time = 0;
    for (const letter of letters) {
      times.push(time);
      time += beat * (0.6 + Math.random() * 0.8);
      // A beat's pause after a space or line end, where the carriage moves on.
      if (letter.classList.contains('is-word-end')) time += beat * 0.6;
    }

    return new Promise((resolve) => {
      const start = performance.now();
      let shown = 0;
      /** @param {number} now */
      const step = (now) => {
        const elapsed = now - start;
        while (shown < letters.length && (times[shown] ?? 0) <= elapsed) {
          letters[shown++]?.classList.add('is-typed');
        }
        if (shown < letters.length) this.#typeFrame = requestAnimationFrame(step);
        else resolve();
      };
      this.#typeFrame = requestAnimationFrame(step);
    });
  }

  /** Tracks whether the summary is in view so the progress bar never covers it. */
  #observeSlip() {
    this.#slipObserver?.disconnect();
    const slip = this.querySelector('.set-builder__slip');
    if (!slip || !this.refs.progress) return;

    this.#slipObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.#slipReached = entry.isIntersecting || entry.boundingClientRect.top < 0;
      this.#update();
    });
    this.#slipObserver.observe(slip);
  }

  /**
   * Keeps each card's checkbox value in sync with its size buttons, then
   * recalculates the order slip.
   * @param {Event} event
   */
  handleChange(event) {
    // The framework routes this from the form, so event.target is the form;
    // the composed path still starts at the input that changed.
    const target = event.composedPath()[0];

    if (target instanceof HTMLInputElement && target.hasAttribute('data-variant-option')) {
      const card = target.closest('[data-card]');
      const checkbox = card?.querySelector('[data-pick]');
      if (checkbox instanceof HTMLInputElement) checkbox.value = target.value;
    }

    this.#update();
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const picks = this.#picks();
    const subscription = this.#subscribing ? this.#subscriptionPlans(picks) : null;
    if (picks.length < this.#minItems || (this.#subscribing && !subscription)) return;

    const cartItemsSectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    const items = this.#cartItems(picks, subscription);

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    this.refs.submit.disabled = true;
    this.refs.submit.setAttribute('aria-busy', 'true');
    this.refs.error.hidden = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body: JSON.stringify({
            items,
            sections: cartItemsSectionIds.join(','),
            // Render the sections for this page (the builder also runs on landing pages).
            sections_url: window.location.pathname,
          }) }),
        signal: abortController.signal,
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.#showError(data.description || data.message);
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description, data.errors));
        return;
      }

      this.#addedPicks = this.#pickKey(picks);
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'set-builder',
          itemCount: items.length,
          sections: data.sections,
        })
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      this.#showError();
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
      this.refs.submit.removeAttribute('aria-busy');
      this.#update();
    }
  }

  /**
   * The /cart/add.js line items for the set. Every cart-payload rule lives here.
   * @param {Pick[]} picks
   * @param {Plan[] | null} subscription - one plan per pick when subscribing
   * @returns {CartItem[]}
   */
  #cartItems(picks, subscription) {
    return picks.map(({ variant }, index) => {
      /** @type {CartItem} */
      const item = { id: variant.id, quantity: 1, properties: { _set: 'build your own' } };
      if (subscription) item.selling_plan = subscription[index].id;
      return item;
    });
  }

  /**
   * The size shown after the product name in the summary: the picked variant
   * when there's a choice, otherwise the block's size label or the variant name.
   * @param {Pick} pick
   */
  #sizeLabel({ card, variant }) {
    if (!card.hasAttribute('data-show-size') && card.dataset.sizeLabel) return card.dataset.sizeLabel;
    return variant.title === 'Default Title' ? '' : variant.title;
  }

  /**
   * Identifies a set of picks, so "already added" survives re-renders.
   * @param {Pick[]} picks
   */
  #pickKey(picks) {
    return picks.map(({ variant }) => variant.id).join(',');
  }

  /** @param {string} [message] */
  #showError(message) {
    this.refs.error.textContent = message || this.dataset.textError || '';
    this.refs.error.hidden = false;
  }

  get #minItems() {
    return Number(this.dataset.minItems) || 1;
  }

  get #subscribing() {
    const checked = this.querySelector('[data-purchase-type]:checked');
    return checked instanceof HTMLInputElement && checked.value === 'subscribe';
  }

  /** @returns {{ min: number, percent: number }[]} */
  get #tiers() {
    return (this.dataset.tiers || '')
      .split(',')
      .map((tier) => {
        const [min, percent] = tier.split(':').map(Number);
        return { min: min || 0, percent: percent || 0 };
      })
      .filter((tier) => tier.min > 0);
  }

  /**
   * @param {HTMLElement} card
   * @returns {Variant[]}
   */
  #variants(card) {
    return JSON.parse(card.querySelector('[data-card-data]')?.textContent || '{"variants":[]}').variants;
  }

  /** @returns {Pick[]} */
  #picks() {
    /** @type {Pick[]} */
    const picks = [];

    for (const card of this.querySelectorAll('[data-card]')) {
      if (!(card instanceof HTMLElement)) continue;
      const checkbox = card.querySelector('[data-pick]');
      if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) continue;

      const variant = this.#variants(card).find((v) => String(v.id) === checkbox.value);
      if (variant) picks.push({ card, variant });
    }

    return picks;
  }

  /**
   * Index of the highest tier reached, or -1.
   * @param {number} count
   */
  #tierIndex(count) {
    let index = -1;
    this.#tiers.forEach((tier, i) => {
      if (count >= tier.min) index = i;
    });
    return index;
  }

  /**
   * The selling plan for each pick at the chosen frequency, preferring the
   * current tier's Appstle group. Null when any pick can't be subscribed.
   * @param {Pick[]} picks
   * @returns {Plan[] | null}
   */
  #subscriptionPlans(picks) {
    const frequencySelect = this.querySelector('[data-frequency]');
    const frequency = frequencySelect instanceof HTMLSelectElement ? frequencySelect.value : null;
    if (!frequency || picks.length === 0) return null;

    const tierGroup = (this.dataset.tierGroups || '').split('|')[this.#tierIndex(picks.length)] || '';
    const defaultGroup = this.dataset.defaultGroup || '';

    /** @type {Plan[]} */
    const plans = [];
    for (const { variant } of picks) {
      const plan =
        variant.plans.find((p) => tierGroup && p.group === tierGroup && p.name === frequency) ||
        variant.plans.find((p) => p.group === defaultGroup && p.name === frequency);
      if (!plan) return null;
      plans.push(plan);
    }
    return plans;
  }

  /** @param {number} cents */
  #money(cents) {
    return new Intl.NumberFormat(this.dataset.locale || undefined, {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    }).format(cents / 100);
  }

  /**
   * @param {string | undefined} template
   * @param {Record<string, string | number>} values
   */
  #text(template, values) {
    return Object.entries(values).reduce(
      (text, [key, value]) => text.replaceAll(`[${key}]`, String(value)),
      template || ''
    );
  }

  /**
   * Rewrites the order slip's lines from the line template.
   * @param {{ pick: Pick, price: number }[]} lines
   */
  #renderLines(lines) {
    const { lines: list, lineTemplate, empty } = this.refs;
    const typewriter = this.#typeObserver !== null;
    const keys = lines.map(({ pick }) => pick.card.getAttribute('aria-labelledby') || pick.card.dataset.name || '');

    // Unpicked items get written out again if they come back.
    for (const key of this.#writtenLines) {
      if (!keys.includes(key)) this.#writtenLines.delete(key);
    }

    list.replaceChildren(
      ...lines.map(({ pick, price }, index) => {
        const fragment = /** @type {DocumentFragment} */ (lineTemplate.content.cloneNode(true));
        const line = fragment.querySelector('.set-builder__line');
        const key = keys[index] ?? '';
        const name = fragment.querySelector('[data-line-name]');
        const size = fragment.querySelector('[data-line-size]');
        const priceElement = fragment.querySelector('[data-line-price]');
        const compareElement = fragment.querySelector('[data-line-compare]');
        if (compareElement) compareElement.textContent = price < pick.variant.price ? this.#money(pick.variant.price) : '';
        if (name) name.textContent = pick.card.dataset.name || '';
        if (size) size.textContent = this.#sizeLabel(pick);
        if (priceElement) priceElement.textContent = this.#money(price);

        // New picks are written onto the slip when it's on screen: tick, then name.
        if (typewriter && line instanceof HTMLElement && !this.#writtenLines.has(key)) {
          const text = line.querySelector('.set-builder__row-text');
          line.dataset.lineKey = key;
          line.classList.add('is-unwritten');
          if (text instanceof HTMLElement) splitLetters(text);
        }
        return fragment;
      })
    );

    if (empty) empty.hidden = lines.length > 0;
    if (this.#linesInView) this.#queueLines();
  }

  #update() {
    if (!this.refs.form) return;

    const picks = this.#picks();
    const count = picks.length;
    const tiers = this.#tiers;
    const tierIndex = this.#tierIndex(count);
    const percent = tierIndex >= 0 ? tiers[tierIndex].percent : 0;
    const nextTier = tiers[tierIndex + 1];

    // Checklist: tick the tier reached, fade the ones passed, nudge toward the next.
    for (const element of this.querySelectorAll('.set-builder__tier')) {
      if (!(element instanceof HTMLElement)) continue;
      const index = Number(element.dataset.tierIndex);
      const isNext = index === tierIndex + 1 && tiers[index];
      element.classList.toggle('is-current', index === tierIndex);
      element.classList.toggle('is-passed', index < tierIndex);
      element.toggleAttribute('aria-current', index === tierIndex);
      const hint = element.querySelector('[data-tier-hint]');
      if (hint) {
        hint.textContent = isNext
          ? this.#text(this.dataset.textNextTier, { count: tiers[index].min - count, percent: tiers[index].percent })
          : '';
      }
    }

    const subscription = this.#subscribing ? this.#subscriptionPlans(picks) : null;
    // Mirrors Bundler's "buy more, save more", which rounds differently by cart
    // type (verified against real carts): one-time takes the tier percent off each
    // item, rounded per item; subscriptions take it off the whole set, rounded up
    // to the cent. A subscription line starts from its Appstle plan price.
    const bases = picks.map((pick, index) => (subscription ? subscription[index].price : pick.variant.price));
    const lines = picks.map((pick, index) => ({ pick, price: bases[index] - Math.round((bases[index] * percent) / 100) }));
    let total = lines.reduce((sum, line) => sum + line.price, 0);
    if (subscription && lines.length) {
      const baseTotal = bases.reduce((sum, base) => sum + base, 0);
      total = baseTotal - Math.ceil((baseTotal * percent) / 100 - 1e-9);
      // The last line absorbs the rounding so the items still add up to the total.
      lines[lines.length - 1].price += total - lines.reduce((sum, line) => sum + line.price, 0);
    }
    const compare = picks.reduce((sum, { variant }) => sum + variant.price, 0);

    /** @type {string[]} */
    const status = [];
    if (count < this.#minItems) {
      status.push(this.#text(this.dataset.textPickMore, { count: this.#minItems - count }));
    } else {
      if (percent > 0) status.push(this.#text(this.dataset.textUnlocked, { percent }));
      if (nextTier) {
        status.push(this.#text(this.dataset.textNextTier, { count: nextTier.min - count, percent: nextTier.percent }));
      }
    }

    this.#renderLines(lines);
    if (this.refs.announce) this.refs.announce.textContent = status.join(' · ');
    this.refs.status.textContent = this.#subscribing && count > 0 && !subscription ? this.dataset.textNoPlan || '' : '';
    this.refs.total.textContent = count > 0 ? this.#money(total) : '—';
    const discounted = count > 0 && total < compare;
    this.refs.compare.textContent = discounted ? this.#money(compare) : '';
    if (this.refs.compareRow) {
      this.refs.compareRow.hidden = count === 0;
      this.refs.compareRow.classList.toggle('has-discount', discounted);
    }
    if (this.refs.compareLabel) this.refs.compareLabel.textContent = this.dataset.textDiscounted || '';
    if (this.refs.fullPriceNote) this.refs.fullPriceNote.hidden = count === 0 || discounted;
    if (this.refs.shippingRow) this.refs.shippingRow.hidden = count === 0;
    if (this.refs.savings) {
      this.refs.savings.textContent =
        count > 0 && total < compare ? this.#text(this.dataset.textSavings, { amount: this.#money(compare - total) }) : '';
    }
    // A set is in progress from the first pick until it's added to the cart.
    const inProgress = count > 0 && this.#pickKey(picks) !== this.#addedPicks;
    document.documentElement.classList.toggle('set-builder-in-progress', inProgress);

    if (this.refs.progress) {
      this.refs.progress.hidden = !inProgress || this.#slipReached;
      this.refs.progress.textContent = this.#text(this.dataset.textProgress, { count });
    }
    this.refs.submit.disabled =
      count < this.#minItems || (this.#subscribing && !subscription) || this.#activeFetch !== null;
  }
}

/** Typewriter pace: a beat per letter, quicker for long blocks, never under the floor. */
const TYPE_BEAT_MS = 24;
const TYPE_MIN_MS = 9;
const TYPE_MAX_TOTAL_MS = 2600;
const TICK_DRAW_MS = 280;

/**
 * Wraps every letter in `root` in its own hidden span for the typewriter.
 * Screen readers get the text once, from a visually hidden copy.
 * @param {HTMLElement} root
 * @returns {number} letters wrapped
 */
function splitLetters(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  /** @type {Text[]} */
  const nodes = [];
  while (walker.nextNode()) {
    const node = /** @type {Text} */ (walker.currentNode);
    if (node.data.trim() && !node.parentElement?.closest('.visually-hidden')) nodes.push(node);
  }

  let count = 0;
  for (const node of nodes) {
    const spoken = document.createElement('span');
    spoken.className = 'visually-hidden';
    spoken.textContent = node.data;

    const shown = document.createElement('span');
    shown.setAttribute('aria-hidden', 'true');
    const chars = [...node.data];
    chars.forEach((char, index) => {
      if (/\s/.test(char)) {
        shown.append(char);
        return;
      }
      const letter = document.createElement('span');
      letter.className = 'set-builder__letter';
      if (index === chars.length - 1 || /\s/.test(chars[index + 1] ?? '')) letter.classList.add('is-word-end');
      letter.textContent = char;
      shown.append(letter);
      count++;
    });

    node.replaceWith(spoken, shown);
  }
  return count;
}

if (!customElements.get('set-builder')) {
  customElements.define('set-builder', SetBuilder);
}
